import React, { useState } from "react";
import { DeleteDialog, Modal } from "../components";
import { sortRows } from "shared/utils/timetablesort";
import Panel from "components/panels/panel";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import Choose from "./LoadFromShow/Choose";
import { ButtonSecondary } from "components/Atoms/Button";
import ShowModalPicker from "components/Picker/ShowPicker/ShowModalPicker";
import { useOpenNewTimetableSidePanel } from "components/SidePanel/Timetable/NewTimetableSidePanel";
import { useQueryClient } from "@tanstack/react-query";
import { apiResponse } from "utilities/apiUtils";
import { useCustomQuery } from "utilities/hookutils";
import { invalidateTimetableCache, timetableQueryKey } from "utilities/api/timetableQuery";
import Table from "components/Tables/Table";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { useOpenTimetableSidePanel } from "components/SidePanel/Timetable/TimetableSidePanel";
import moment from "moment";
import { formatTimeRange } from "shared/utils/momentutils";
import { formatTime, newLineToBr } from "utilities";

interface Props {
  showId: number
  panelConfig: server.dto.TimetablePanelConfig
}

const useTimetablesApi = (showId: number) => useCustomQuery({
  queryKey: [timetableQueryKey, showId],
  queryFn: async () => window.CoreApi.Timetable.ListTimetableEntries(showId).then(r => r.data),
  initialData: apiResponse.success([])
});

const TimetablePanel = ({ showId, panelConfig }: Props) => {
  const [displayChooseOptionModal, setDisplayChooseOptionModal] = useState<boolean>(false);
  const [displayLoadFromShowModal, setDisplayLoadFromShowModal] = useState<boolean>(false);
  const [deletingEntryId, setDeletingEntryId] = useState<number>();

  const timetablesApi = useTimetablesApi(showId);
  const queryClient = useQueryClient();
  const openNewTimetableSidePanel = useOpenNewTimetableSidePanel();
  const openTimetableSidePanel = useOpenTimetableSidePanel();

  const onNewRowClicked = () => {
    openNewTimetableSidePanel(showId);
  };

  const onDisplayChooseOptionModal = () => {
    setDisplayChooseOptionModal(true);
  };

  const loadFromShowExcludeKeys = [showId];
  const loadFromShowInstructionOverrides: Partial<server.dto.PaginatedShowInternalInstruction> = {
    hasTimetable: true,
    onlyMySelection: false
  };

  const onShowSelectedForTimetableCopy = async (show: server.dto.ShowForListInternal) => {
    await window.CoreApi.Timetable.Duplicate({
      sourceShowId: show.showId,
      targetShowId: showId
    });
    invalidateTimetableCache(queryClient);
    setDisplayLoadFromShowModal(false);
  };

  const onDeleteEntry = async () => {
    await window.CoreApi.Timetable.DeleteEntry({ timetableEntryId: deletingEntryId }, { toastOnError: true });
    setDeletingEntryId(undefined);
    invalidateTimetableCache(queryClient);
  };

  const formatEntryTime = (entry: server.dto.TimetableEntry) => {
    const start = entry.startTime ? moment.utc(entry.startTime) : undefined;
    const end = entry.endTime ? moment.utc(entry.endTime) : undefined;
    const isSameTime = start != null && end != null && start.isSame(end);

    return formatTimeRange(
      start ? formatTime(start) : undefined,
      end && !isSameTime ? formatTime(end) : undefined
    );
  };

  let addButton: JSX.Element = null;
  if (panelConfig.canModifyTimetable) {
    if (!panelConfig.canCopyTimetableFromShow || timetablesApi.data.data.length > 0) {
      addButton = <ButtonSecondary onClick={onNewRowClicked} label={RESX.Buttons.Add} />;
    } else {
      addButton = <ButtonSecondary onClick={onDisplayChooseOptionModal} label={RESX.Buttons.btnActivate} />;
    }
  }

  const { refToScrollTo } = useShortcutPanel({
    type: "timetable",
    label: panelConfig.headerLabel,
    totalItems: timetablesApi.data.data.length,
    onAddClicked: panelConfig.canModifyTimetable ? onNewRowClicked : undefined
  });

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={panelConfig.headerLabel}
      button={addButton}
      articleId={serverReference.supportArticleIds.whatIsATimetable}
      isLoading={timetablesApi.isLoading}
      hasError={timetablesApi.isError || timetablesApi.data.hasErrors}
      hasData={timetablesApi.data.data.length > 0}
    >
      <Table
        items={[...timetablesApi.data.data].sort(sortRows)}
        getKey={entry => entry.timetableEntryId.toString()}
        isLoading={timetablesApi.isLoading}
        hideHeader
        onDelete={panelConfig.canModifyTimetable ? entry => setDeletingEntryId(entry.timetableEntryId) : undefined}
        columns={[{
          key: "time",
          header: "",
          render: entry => <SmallDetails
            label={formatEntryTime(entry)}
            subLabel={entry.nextDay && RESX.DateTime.NextDay}
          />
        }, {
          key: "type",
          header: "",
          render: entry => panelConfig.canModifyTimetable
            ? <a onClick={() => openTimetableSidePanel(entry, panelConfig.canModifyTimetable)}>{entry.type.text}</a>
            : <span>{entry.type.text}</span>
        }, {
          key: "notes",
          header: "",
          render: entry => <div style={{ wordBreak: "break-word" }}>{newLineToBr(entry.notes)}</div>
        }]}
      />
    </Panel>

    <DeleteDialog
      spinForever={false}
      isOpen={deletingEntryId != null}
      onDelete={onDeleteEntry}
      handleClose={() => { setDeletingEntryId(undefined); }}
      message={RESX.GeneralWarnings.DeleteSure}
    />

    <Modal
      title={RESX.Timetable.xTimetable}
      isOpen={displayChooseOptionModal}
      handleClose={() => { setDisplayChooseOptionModal(false); }}
    >
      <Choose
        onCreateNewRowSelected={() => {
          setDisplayChooseOptionModal(false);
          onNewRowClicked();
        }}
        onCopyFromShowSelected={() => {
          setDisplayChooseOptionModal(false);
          setDisplayLoadFromShowModal(true);
        }}
      />
    </Modal>

    {displayLoadFromShowModal && <ShowModalPicker
      canCreateNew={false}
      modalTitle={RESX.Timetable.xTimetable}
      defaultFilter={panelConfig.defaultFilters}
      initialInstruction={panelConfig.initialInstruction}
      excludeKeys={loadFromShowExcludeKeys}
      instructionOverrides={loadFromShowInstructionOverrides}
      onModalClose={() => { setDisplayLoadFromShowModal(false); }}
      inPageEditing={{
        hasValue: true,
        cancel: () => { setDisplayLoadFromShowModal(false); },
        onChange: (show) => { onShowSelectedForTimetableCopy(show); }
      }}
    />}
  </>;
};

export default TimetablePanel;
